import { DialogType } from '@tableau/widgets';
import React = require('react');
import * as _ from 'underscore';
import { SetSiteUsersRoleDialog } from '../../app/users/SetSiteUsersRoleDialog';
import { CommonStyles } from '../../common/react/CommonStyles';
import { SiteRolePickerMenu } from '../../common/react/components/SiteRolePickerMenu';
import { AngularContextFactory } from '../factories/AngularContextFactory';
import { FeatureFlags } from '../FeatureFlags';
import { IActionContext } from '../helpers/ActionContext';
import { CommonErrorCode } from '../helpers/ErrorCodes';
import { L10n } from '../L10n/L10n';
import { ISiteUserInfo } from '../models/ISiteUserInfo';
import { IUserInfo } from '../models/IUserInfo';
import { Set } from '../models/Set';
import { Module } from '../Module';
import { SiteRoleOption, SiteRoleOptionsResource } from '../resources/SiteRoleOptions';
import { SiteRolesResource } from '../resources/SiteRoles';
import { SiteUsers } from '../resources/SiteUsers';
import { IUpdateUsersAdminStatusResult } from '../ServerApi/requests/UpdateUsersAdminStatusRequest';
import { IError } from '../ServerApi/types/IError';
import { IResult } from '../ServerApi/types/IResponse';
import { SiteRoles } from '../ServerApi/types/ISiteRole';
import { ConfirmActionDialog, IConfirmActionDialogInstance } from './ConfirmActionDialog';
import { ModalInstance, ReactModalService } from './ReactModalService';
import { ServerService } from './ServerService';
import { ServerAdminUpdateActionNotification, SiteRolesUpdateActionNotification } from './UserActionNotification';


const SetSiteUsersRoleDialogStyle: React.CSSProperties = {
  width: 'auto',
  height: 'auto',
  paddingTop: CommonStyles.Sizes.MediumSpace,
  paddingLeft: CommonStyles.Sizes.MediumSpace,
  paddingRight: CommonStyles.Sizes.MediumSpace
};
export interface ISetUsersSiteRoleDialogScope extends ng.IScope {
  description: string;
  siteRole: string;
  siteRoleOptions: string[];
  setSiteRole: (role: string) => void;
  shouldShowLicensingTierInfo: boolean;
  siteRoleSelectorProps: SiteRolePickerMenu.Props;
}

export class SetSiteUsersSiteRoleAction {

  public static $inject = ['$translate',
                           '$q',
                           'AngularContextFactory',
                           'ConfirmActionDialog',
                           'ServerAdminUpdateActionNotification',
                           'ServerService',
                           'SiteRoleOptionsResource',
                           'SiteRoles',
                           'SiteRolesUpdateActionNotification',
                           'SiteUsers'];

  constructor(
    private $translate: ng.translate.ITranslateService,
    private $q: ng.IQService,
    private angularContextFactory: AngularContextFactory,
    private confirmActionDialog: ConfirmActionDialog,
    private serverAdminUpdateActionNotification: ServerAdminUpdateActionNotification,
    private serverService: ServerService,
    private siteRoleOptionsResource: SiteRoleOptionsResource,
    private siteRoles: SiteRolesResource,
    private siteRolesUpdateActionNotification: SiteRolesUpdateActionNotification,
    private siteUsers: SiteUsers) { }

  public showSetSiteUsersSiteRoleDialog(siteUsersToSet: Set<ISiteUserInfo>,
    context: IActionContext): ng.IPromise<any> {
    if (FeatureFlags.SetSiteUsersRoleDialogReactEnabled) {
      let modalInstance: ModalInstance;
      const dialogProps = {
        close: () => modalInstance && modalInstance.close({}),
        dismiss: () => modalInstance && modalInstance.dismiss('cancel'),
        angularContext: this.angularContextFactory.create(context.scope),
        siteUsersToSet: siteUsersToSet
      };

      modalInstance = ReactModalService.open({
        type: DialogType.Uber,
        dialogContent: React.createElement(SetSiteUsersRoleDialog, dialogProps),
        testId: 'setSiteUsersRoleDialog',
        showCloseButton: true,
        dialogStyle: SetSiteUsersRoleDialogStyle,
        shouldNotSetDefaultWidth: true
      });
      return modalInstance.result;
    }

    const dialogScope = <ISetUsersSiteRoleDialogScope> context.scope.$new();
    dialogScope.siteRole = (siteUsersToSet.size() === 1) ? siteUsersToSet.first().siteRole : SiteRoles.Unlicensed;

    dialogScope.siteRoleOptions = this.siteRoles.optionsForCurrentUser();
    dialogScope.setSiteRole = (role: string) => {
      dialogScope.siteRole = role;
    };

    dialogScope.shouldShowLicensingTierInfo = this.serverService.shouldShowServerLicensingTierInfo();

    const onSiteRoleSelected = (roleOption: SiteRoleOption) => {
      dialogScope.$apply(() => {
        dialogScope.siteRoleSelectorProps.selectedRole = roleOption;
        dialogScope.setSiteRole(roleOption.role);
      });
    };

    const SiteUserSiteRoleDropdownWidth = 260;

    dialogScope.siteRoleSelectorProps = {
      translate: this.$translate.instant,
      siteRoleOptions: dialogScope.siteRoleOptions.map((siteRole: string) => this.siteRoleOptionsResource.getSiteRoleOptionBySiteRole(siteRole)),
      getSiteRoleOptionBySiteRole: (siteRole: string) => this.siteRoleOptionsResource.getSiteRoleOptionBySiteRole(siteRole),
      selectedRole: this.siteRoleOptionsResource.getSiteRoleOptionBySiteRole(dialogScope.siteRole),
      onOptionSelected: onSiteRoleSelected,
      style: { width: SiteUserSiteRoleDropdownWidth - CommonStyles.Sizes.DefaultSpace - (2 * CommonStyles.Sizes.TextBoxBorderSize) }
    };

    dialogScope.description = this.$translate.instant('confirmSetUsersSiteRoleDesc', {
      USER_NAME: L10n.elideIfLong(siteUsersToSet.first().displayName),
      USER_COUNT: siteUsersToSet.size()
    }, L10n.Interpolations.Count);

    const dialogInstance = this.confirmActionDialog.open({
      title: this.$translate.instant('confirmSetUsersSiteRoleTitle'),
      bodyTemplateUrl: 'confirmActionDialogInputRole.html',
      scope: dialogScope,
      confirmDisabled: () => false, // 'confirm' is always enabled for this action.
      actionName: this.$translate.instant('confirmSetUsersSiteRoleButton', {
        ITEM_COUNT: siteUsersToSet.size()
      }, L10n.Interpolations.Count),
      action: (dialogInstance: IConfirmActionDialogInstance) => this.setSiteUsersSiteRoles(siteUsersToSet, dialogScope.siteRole)
    });

    dialogInstance.result.finally(() => dialogScope.$destroy());

    return dialogInstance.result;
  }

  private setSiteUsersSiteRoles(selectedUsers: Set<IUserInfo>, siteRole: string): ng.IPromise<any> {
    const users = selectedUsers.clone();
    if (siteRole === SiteRoles.ServerAdministrator) {
      return this.makeUsersServerAdmins(users)
        .then((showToastForPromotedAdmins: () => void) =>
          showToastForPromotedAdmins());
    } else {
      // demote server admins
      // update site roles
      // show toast for both after complete to avoid toast showing up behind dialog.
      let showToastForDemotedAdmins = () => {};
      const serverAdmins = users.filter((user: ISiteUserInfo) => user.siteRole === SiteRoles.ServerAdministrator);
      return this.demoteServerAdminsNoDelay(serverAdmins)
        .then((result: IResult) => {
          // don't try update site role for admins that did not get demoted
          if (!!result.errors) {
            _.each(result.errors, (error: IError) => {
              users.remove(users.get(error.id));
            });
          }
          showToastForDemotedAdmins = (() => this.serverAdminUpdateActionNotification.notify(result, serverAdmins, false));
          return this.updateUsersSiteRole(users, siteRole);
        })
        .then((showToastForUpdatedUsersSiteRoles: () => void) => {
          showToastForDemotedAdmins();
          // we might not have any users whose roles were updated, if they were removed from errors in updating admin status.
          // if we don't have any users whose roles were updated, we'll only show the demoted admins toast.
          if (users.size() > 0) {
            showToastForUpdatedUsersSiteRoles();
          }
        });
    }
  }

  // if any selected users are server admins && current user is server admin, demote!
  private demoteServerAdminsNoDelay(serverAdmins: Set<IUserInfo>): ng.IPromise<IResult> {
    if (serverAdmins.size() === 0) {
      return this.$q.when({});
    } else if (!this.serverService.isSessionServerAdmin()) {
      // hand craft an IResult with errors
      const result: IResult = { errors: [] };
      serverAdmins.each((user: ISiteUserInfo) => {
        result.errors.push({
          id: user.id,
          code: CommonErrorCode.CallingUserInsufficientPermissions
        });
      });
      return this.$q.when(result);
    } else {
      return this.siteUsers.updateServerAdminStatusNoDelay(serverAdmins.keys(), false);
    }
  }

  private updateUsersSiteRole(users: Set<IUserInfo>, siteRole: string): ng.IPromise<any> {
    return this.siteUsers.updateUsersSiteRole(users.keys(), siteRole).then((result: IResult) => {
      // returns a function that shows toast for the action
      return () => this.siteRolesUpdateActionNotification.notify(result, users, siteRole);
    });
  }

  // makes the users server admins and shows toast
  private makeUsersServerAdmins(users: Set<IUserInfo>): ng.IPromise<any> {
    return this.siteUsers.updateServerAdminStatusAndDelay(users.keys(), true).then((result: IUpdateUsersAdminStatusResult) => {
      // returns a function to show toast for the action
      return () => this.serverAdminUpdateActionNotification.notify(result, users, true);
    });
  }

}

Module.instance().service('SetSiteUsersSiteRoleAction', SetSiteUsersSiteRoleAction);
